import React, { useEffect, useMemo, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import axios from 'axios';
import constants from '../../const';

const { GARMENT_API } = constants;

const SEARCH_FIELDS = [
    'name', 'color', 'category_name', 'style_name', 'pattern_name',
    'texture_name', 'material_name', 'cut', 'straps', 'description'
];

const getFlatLayPath = (garment) => {
    if (garment.flatlay_url) return garment.flatlay_url;
    if (garment.image_url) return garment.image_url.replace('.webp', '_flatlay.webp');
    return null;
};

const GarmentCard = ({ garment }) => {
    const flatLayPath = getFlatLayPath(garment);
    return (
    <div style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden', background: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}>
        <div style={{ position: 'relative', aspectRatio: '3 / 4', background: '#f3f3f3' }}>
            {flatLayPath ? (
                <img
                    src={`${constants.CONTENT_DOMAIN}${flatLayPath}`}
                    alt={garment.name}
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
            ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: '40px' }}>👗</div>
            )}
            <span
                title="Scenes"
                style={{ position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.7)', color: '#fff', borderRadius: '12px', padding: '2px 8px', fontSize: '12px' }}
            >
                🎬 {garment.scene_count ?? 0}
            </span>
        </div>
        <div style={{ padding: '8px 10px' }}>
            <div title={garment.name} style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{garment.name}</div>
            <div className="text-muted" style={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {[garment.category_name, garment.color].filter(Boolean).join(' · ')}
            </div>
        </div>
    </div>
    );
};

function ActorGarmentGrid({ actorId }) {
    const [garments, setGarments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await axios.get(`${GARMENT_API.GARMENT_BY_ACTOR_URL}/${actorId}`, {
                    headers: { 'Authorization': `${localStorage.getItem('token')}` }
                });
                if (!cancelled) setGarments(res.data.garments || []);
            } catch (err) {
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [actorId]);

    const filtered = useMemo(() => {
        const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
        if (terms.length === 0) return garments;
        return garments.filter(g =>
            terms.every(term => SEARCH_FIELDS.some(field => g[field] && String(g[field]).toLowerCase().includes(term)))
        );
    }, [garments, search]);

    if (loading) {
        return <div className="text-center" style={{ paddingTop: '50px' }}><Spinner animation="border" /></div>;
    }

    if (error) {
        return <div className="text-center text-danger" style={{ paddingTop: '50px' }}>Failed to load garments: {error}</div>;
    }

    return (
        <div style={{ paddingTop: '30px' }}>
            <Form.Control
                type="search"
                placeholder="Search garments (name, color, category, style, pattern...)"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ maxWidth: '420px' }}
            />
            <div className="text-muted" style={{ marginTop: '8px', fontSize: '14px' }}>
                {filtered.length} of {garments.length} garments
            </div>
            {filtered.length === 0 ? (
                <p style={{ paddingTop: '30px', textAlign: 'center' }}>
                    {garments.length === 0 ? 'No garments for this actor yet.' : 'No garments match your search.'}
                </p>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px', marginTop: '16px' }}>
                    {filtered.map(g => <GarmentCard key={g.id} garment={g} />)}
                </div>
            )}
        </div>
    );
}

export default ActorGarmentGrid;
